import {
	deleteInvoicesByID,
	invoicesByID,
	invoicesList,
	newInvoices,
	updateInvoices,
	invoicesStats,
	updateInvoiceStatus,
	sendPaymentLink,
	downloadReceipt,
	resendReceipt,
	generateReceipt,
} from '@/services/invoices.service'

export default {
	namespaced: true,
	state: {
		// INVOICES
		invoicesList: {},
		invoices: {},
		invoicesStats: {},

		// STATUS
		invoicesStatus: 'init',
		invoicesSaveStatus: 'init',
		invoicesDeleteStatus: 'init',
	},
	getters: {
		// INVOICES
		getInvoicesList(state) {
			return state.invoicesList
		},
		getInvoices(state) {
			return state.invoices
		},

		// STATUS
		getInvoicesDeleteStatus(state) {
			return state.invoicesDeleteStatus
		},
		getInvoicesSaveStatus(state) {
			return state.invoicesSaveStatus
		},
		getInvoicesStatus(state) {
			return state.invoicesStatus
		},
		getInvoicesStats(state) {
			return state.invoicesStats
		},
	},
	mutations: {
		// INVOICES
		INVOICES_LIST(state, value) {
			state.invoicesList = value
		},
		SET_INVOICES(state, value) {
			state.invoices = value
		},

		// STATUS
		INVOICES_STATUS(state, value) {
			state.invoicesStatus = value
		},
		INVOICES_SAVE_STATUS(state, value) {
			state.invoicesSaveStatus = value
		},
		INVOICES_DELETE_STATUS(state, value) {
			state.invoicesDeleteStatus = value
		},
		INVOICES_STATS(state, value) {
			state.invoicesStats = value
		}
	},
	actions: {
		// Invoices
		async fetchInvoicesList({ commit }, params) {
			commit('INVOICES_STATUS', 'pending')
			try {
				const { data } = await invoicesList(params)
				commit('INVOICES_STATUS', 'init')
				commit('INVOICES_LIST', data)
				return data
			} catch (error) {
				commit('INVOICES_STATUS', 'init')
				this._vm.$root.$notify({
					message: error.response.data.message,
					type: 'danger',
				})
				throw error
			}
		},
		async fetchInvoicesByID({ state, commit }, params) {
			commit('INVOICES_STATUS', 'pending')
			try {
				const { data } = await invoicesByID(params)
				commit('INVOICES_STATUS', 'init')
				commit('SET_INVOICES', data.data)
				return data
			} catch (error) {
				commit('INVOICES_STATUS', 'init')
				// `return error` (not `throw`) made this resolve successfully on
				// failure — new.vue's own try/catch around this call never saw a
				// rejection, so its redirect-back-to-the-list-on-error branch never
				// ran, and the page just sat on the blank edit form. Re-throwing
				// lets that handling actually fire; no $notify here (the only
				// caller already shows its own, more specific one) so the error
				// doesn't toast twice.
				throw error
			}
		},
		async saveNewInvoices({ commit }, params) {
			commit('INVOICES_SAVE_STATUS', 'pending')
			try {
				const { data } = await newInvoices(params)
				commit('INVOICES_SAVE_STATUS', 'init')
				return data
			} catch (error) {
				commit('INVOICES_SAVE_STATUS', 'init')
				this._vm.$root.$notify({
					message: error.response.data.message,
					type: 'danger',
				})
				throw error
			}
		},
		async updateInvoices({ commit }, params) {
			commit('INVOICES_SAVE_STATUS', 'pending')
			try {
				const { data } = await updateInvoices(params)
				commit('INVOICES_SAVE_STATUS', 'init')
				return data
			} catch (error) {
				commit('INVOICES_SAVE_STATUS', 'init')
				this._vm.$root.$notify({
					message: error.response.data.message,
					type: 'danger',
				})
				throw error
			}
		},
		async deleteInvoicesByID({ state, commit }, params) {
			commit('INVOICES_DELETE_STATUS', 'pending')
			try {
				const { data } = await deleteInvoicesByID(params)
				commit('INVOICES_DELETE_STATUS', 'init')
				return data
			} catch (error) {
				commit('INVOICES_DELETE_STATUS', 'error')
				this._vm.$root.$notify({
					message: error.response.data.message,
					type: 'danger',
				})
				return error
			}
		},
		async updateInvoiceStatus({ commit }, { id, status, payment_gateway }) {
			try {
				const { data } = await updateInvoiceStatus(id, { status, payment_gateway })
				return data
			} catch (error) {
				this._vm.$root.$notify({
					message: error.response.data.message,
					type: 'danger',
				})
				throw error
			}
		},
		async sendPaymentLink({ commit }, id) {
			try {
				const { data } = await sendPaymentLink(id)
				return data
			} catch (error) {
				this._vm.$root.$notify({
					message: error.response.data.message,
					type: 'danger',
				})
				throw error
			}
		},
		async downloadInvoiceReceipt({ commit }, invoice) {
			try {
				const response = await downloadReceipt(invoice.id)
				const blob = new Blob([response.data], { type: 'application/pdf' })
				const url = window.URL.createObjectURL(blob)
				const link = document.createElement('a')
				link.href = url
				link.download = `receipt-${invoice.reference || invoice.id}.pdf`
				document.body.appendChild(link)
				link.click()
				document.body.removeChild(link)
				window.URL.revokeObjectURL(url)
				return true
			} catch (error) {
				this._vm.$root.$notify({
					message:
						error.response?.data?.message || 'Failed to download receipt',
					type: 'danger',
				})
				throw error
			}
		},
		async resendInvoiceReceipt({ commit }, id) {
			try {
				const { data } = await resendReceipt(id)
				return data
			} catch (error) {
				this._vm.$root.$notify({
					message: error.response?.data?.message || 'Failed to resend receipt',
					type: 'danger',
				})
				throw error
			}
		},
		async generateInvoiceReceipt({ commit }, id) {
			try {
				const { data } = await generateReceipt(id)
				return data
			} catch (error) {
				this._vm.$root.$notify({
					message: error.response?.data?.message || 'Failed to queue receipt generation',
					type: 'danger',
				})
				throw error
			}
		},
		async fetchInvoicesStats({ commit }, params) {
			commit('INVOICES_STATS', {})
			try {
				const { data } = await invoicesStats(params)
				commit('INVOICES_STATS', data)
				return data
			} catch (error) {
				this._vm.$root.$notify({
					message: error.response.data.message,
					type: 'danger',
				})
				throw error
			}
		},
	},
}
